import { api, ApiError } from "../api.js";
import { el } from "./dom.js";
import { USERNAME_MAX, USERNAME_MIN } from "@webolo/shared";

export function authScreen(onAuthed: (username: string) => void): HTMLElement {
  let mode: "login" | "register" = "login";

  const error = el("div", { class: "auth-error" });
  const username = el("input", { type: "text", autocomplete: "username", maxlength: USERNAME_MAX });
  // Email is collected on signup only; hidden in login mode so the existing
  // "USERNAME + PASSWORD" flow stays untouched for returning players.
  const email = el("input", { type: "email", autocomplete: "email", maxlength: 254 });
  const emailField = el(
    "div",
    { class: "field" },
    el("label", {}, "EMAIL"),
    el("div", { class: "ipt" }, el("span", { class: "caret" }, "▶"), email),
  );
  emailField.style.display = "none";
  const password = el("input", { type: "password", autocomplete: "current-password", maxlength: 128 });
  const submit = el("button", { class: "btn btn-rust auth-submit", type: "submit" }, "LOG IN");
  const tabLogin = el("button", { type: "button", class: "tab active" }, "LOG IN");
  const tabRegister = el("button", { type: "button", class: "tab" }, "CREATE ACCOUNT");

  const panelTitle = el("span", { class: "pt-title" }, "ACCESS TERMINAL");
  const panelSub = el("span", { class: "pt-sub" }, "// LOG IN");

  function setMode(next: "login" | "register"): void {
    mode = next;
    tabLogin.classList.toggle("active", mode === "login");
    tabRegister.classList.toggle("active", mode === "register");
    const isLogin = mode === "login";
    submit.textContent = isLogin ? "LOG IN" : "CREATE ACCOUNT";
    panelSub.textContent = isLogin ? "// LOG IN" : "// REGISTER";
    password.setAttribute("autocomplete", isLogin ? "current-password" : "new-password");
    emailField.style.display = isLogin ? "none" : "";
    error.textContent = "";
  }

  tabLogin.addEventListener("click", () => setMode("login"));
  tabRegister.addEventListener("click", () => setMode("register"));

  async function onSubmit(ev: Event): Promise<void> {
    ev.preventDefault();
    error.textContent = "";
    const u = username.value.trim();
    const p = password.value;
    const e = email.value.trim();
    if (!u || !p) {
      error.textContent = "Enter a username and password.";
      return;
    }
    if (mode === "register" && !e) {
      error.textContent = "Enter your email address.";
      return;
    }
    submit.setAttribute("disabled", "");
    try {
      const resp = mode === "login"
        ? await api.login({ username: u, password: p })
        : await api.register({ username: u, password: p, email: e });
      onAuthed(resp.username);
    } catch (e) {
      error.textContent = humanError(e);
    } finally {
      submit.removeAttribute("disabled");
    }
  }

  const form = el(
    "form",
    { class: "auth-form", onsubmit: onSubmit },
    el(
      "div",
      { class: "field" },
      el("label", {}, "USERNAME"),
      el("div", { class: "ipt" }, el("span", { class: "caret" }, "▶"), username),
    ),
    emailField,
    el(
      "div",
      { class: "field" },
      el("label", {}, "PASSWORD"),
      el("div", { class: "ipt" }, el("span", { class: "caret" }, "▶"), password),
    ),
    error,
    submit,
  );

  const pitch = el(
    "section",
    { class: "panel pitch" },
    el("span", { class: "rivet tl" }),
    el("span", { class: "rivet tr" }),
    el("span", { class: "rivet bl" }),
    el("span", { class: "rivet br" }),
    el(
      "div",
      { class: "logo-wrap" },
      el("span", { class: "logo-we" }, "WE"),
      el("span", { class: "logo-bolo" }, "BOLO"),
    ),
    el("p", { class: "tagline" }, "Classic tank combat,\nreborn in your browser."),
    el(
      "ul",
      {},
      el("li", {}, "Totally free — no ads, no catch"),
      el("li", {}, "Multiplayer made easy — find a game in seconds"),
      el("li", {}, "No download required — runs right in your browser"),
      el("li", {}, "The 90s Bolo classic, kept alive"),
    ),
    el(
      "p",
      { class: "about" },
      "Bolo is a legendary Mac multiplayer tank game from 1987. WEBolo is a passion project to bring it back — with the same maps, the same chaos, and a lobby that makes jumping into a game effortless.",
    ),
  );

  const authCard = el(
    "section",
    { class: "panel auth-card" },
    el("span", { class: "rivet tl" }),
    el("span", { class: "rivet tr" }),
    el("span", { class: "rivet bl" }),
    el("span", { class: "rivet br" }),
    el("header", { class: "panel-head" }, panelTitle, panelSub),
    el(
      "div",
      { class: "auth-body" },
      el("div", { class: "auth-tabs" }, tabLogin, tabRegister),
      form,
    ),
  );

  return el(
    "div",
    { class: "boot" },
    el("div", { class: "boot-frame" }, pitch, authCard),
  );
}

function humanError(e: unknown): string {
  if (e instanceof ApiError) {
    const code = typeof e.body === "object" && e.body && "error" in e.body ? (e.body as { error: string }).error : "";
    switch (code) {
      case "username_taken": return "That username is already taken.";
      case "email_taken": return "That email is already registered.";
      case "invalid_credentials": return "Wrong username or password.";
      case "invalid_input": return `Check your username (${USERNAME_MIN}-${USERNAME_MAX} chars), email, and password (min 8 chars).`;
      default: return code || `Error ${e.status}`;
    }
  }
  return "Network error. Try again.";
}
